// Tab.jsx

import React from 'react';

// SVG
import Icon from 'svg/Icon';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './Tab.css';

/**
 * Tab
 *
 * Displays a single tab.
 *
 * @param {string} label - The label to display on the tab
 * @param {React.Component} icon - (Optional) Icon component to display on the tab
 * @param {string} iconKey - (Optional) Icon key to display on the tab
 * @param {string} tabKey - Stable key for tab measurement
 * @param {Boolean} isActive - Whether this tab is currently active
 * @param {ReactNode} rightContent - Optional right-side content for the tab
 * @param {Function} onClick - Click handler for the tab
 * @param {Number} width - (Optional) Fixed width for the tab
 * @param {Function} tabRef - Ref callback for tab element
 *
 * @returns {ReactNode} - Tab.
 */
const Tab = React.memo(({ label, icon: IconComponent, iconKey, tabKey, isActive, rightContent = null, width, showLabel = true, onClick, tabRef }) => {

    // Theme Context
    const { theme } = useTheme();

    return (
        <div
            className={`tab-container ${isActive ? 'active' : ''}${rightContent ? ' has-right-content' : ''}`}
            ref={tabRef}
            data-tab-key={tabKey}
            style={{
                color: theme.foreground,
                width: width ? `${width}px` : 'auto',
            }}
            onClick={onClick}
        >
            <div className="tab-body">
                {iconKey ? (
                    <Icon
                        iconKey={iconKey}
                        color={theme.foreground}
                        width={18}
                        height={18}
                    />
                ) : IconComponent ? (
                    <IconComponent
                        color={theme.foreground}
                        width={18}
                        height={18}
                    />
                ) : null}

                {showLabel ? <span className="tab-label">{label}</span> : null}
            </div>

            {rightContent ? (
                <div
                    className="tab-right-content"
                    onClick={(event) => event.stopPropagation()}
                >
                    {rightContent}
                </div>
            ) : null}
        </div>
    );
});

Tab.displayName = 'Tab';

export default Tab;

